/**
 * Cookie Consent Manager
 * Manages cookie consent using Google Advanced Consent Mode
 */
export default class CookieConsent {
  constructor() {
    this.consentKey = "hoanmy_cookie_consent";
    this.consentValue = {
      accepted: false,
      timestamp: null,
    };

    // Initialize dataLayer for Advanced Consent Mode
    // Note: GTM script is loaded from PHP (header.php) to comply with CSP
    // We only need to update consent state if it changed
    this.initDataLayer();

    this.init();
  }

  initDataLayer() {
    // Initialize dataLayer if it doesn't exist
    window.dataLayer = window.dataLayer || [];

    // Check if consent has been given previously
    const storedConsent = this.getConsent();
    const consentGranted = storedConsent !== null && storedConsent.accepted;

    // Note: Initial consent state is set in PHP (header.php) before GTM loads
    // This function only updates if consent state needs to change
    // The PHP script already sets the default consent state, so we only
    // need to update if the user changes their consent preference
    if (storedConsent !== null) {
      // User has made a choice before, ensure consent state matches
      // This handles cases where page loads with existing consent
      this.updateConsentState(consentGranted);
    }
  }

  init() {
    // Check if consent has been given
    const storedConsent = this.getConsent();

    if (storedConsent === null) {
      // No consent stored, show banner
      this.showBanner();
    } else {
      // Consent already stored, hide banner
      this.hideBanner();
    }

    // Bind event handlers
    this.bindEvents();
  }

  bindEvents() {
    const $acceptBtn = $("#cookie-accept-btn");
    const $rejectBtn = $("#cookie-reject-btn");

    if ($acceptBtn.length) {
      $acceptBtn.on("click", () => {
        this.acceptCookies();
      });
    }

    if ($rejectBtn.length) {
      $rejectBtn.on("click", () => {
        this.rejectCookies();
      });
    }
  }

  showBanner() {
    const $banner = $("#cookie-consent-banner");
    if ($banner.length) {
      $banner.fadeIn(300);
    }
  }

  hideBanner() {
    const $banner = $("#cookie-consent-banner");
    if ($banner.length) {
      $banner.fadeOut(300, function () {
        $(this).remove();
      });
    }
  }

  acceptCookies() {
    this.consentValue = {
      accepted: true,
      timestamp: new Date().getTime(),
    };

    this.saveConsent();
    this.hideBanner();

    // Update consent state in dataLayer (Advanced Consent Mode)
    this.updateConsentState(true);
  }

  rejectCookies() {
    this.consentValue = {
      accepted: false,
      timestamp: new Date().getTime(),
    };

    this.saveConsent();
    this.hideBanner();

    // Update consent state in dataLayer (Advanced Consent Mode)
    this.updateConsentState(false);
  }

  updateConsentState(granted) {
    // Update consent state using gtag for Google Consent Mode
    // This must be executed immediately when user clicks Accept/Reject
    if (typeof gtag !== "undefined") {
      gtag("consent", "update", {
        ad_storage: granted ? "granted" : "denied",
        ad_user_data: granted ? "granted" : "denied",
        ad_personalization: granted ? "granted" : "denied",
        analytics_storage: granted ? "granted" : "denied",
      });
    } else {
      // Fallback to dataLayer if gtag is not available
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "consent_update",
        ad_storage: granted ? "granted" : "denied",
        ad_user_data: granted ? "granted" : "denied",
        ad_personalization: granted ? "granted" : "denied",
        analytics_storage: granted ? "granted" : "denied",
      });
    }
  }

  saveConsent() {
    try {
      localStorage.setItem(this.consentKey, JSON.stringify(this.consentValue));
    } catch (e) {
      console.error("Failed to save cookie consent:", e);
      // Fallback to cookie if localStorage fails
      this.setCookie(this.consentKey, JSON.stringify(this.consentValue), 365);
    }
  }

  getConsent() {
    try {
      const stored = localStorage.getItem(this.consentKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      // Try cookie fallback
      const cookieValue = this.getCookie(this.consentKey);
      if (cookieValue) {
        try {
          return JSON.parse(cookieValue);
        } catch (e2) {
          console.error("Failed to parse cookie consent:", e2);
        }
      }
    }
    return null;
  }

  // Note: loadGTM() method removed
  // GTM script is now loaded from PHP (header.php) to comply with Content Security Policy
  // This prevents CSP violations from dynamically creating script elements in JavaScript

  // Cookie fallback methods
  setCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`;
  }

  getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        return c.substring(nameEQ.length, c.length);
      }
    }
    return null;
  }

  // Public method to check if consent is given
  hasConsent() {
    const consent = this.getConsent();
    return consent !== null && consent.accepted === true;
  }
}
